import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getLanguageOptions } from '../config/languagesConfig';

// Simple dictionary for UI strings
export const translations = {
  en: {
    home: "Home",
    explore: "Explore",
    stories: "Stories",
    journal: "Journal",
    vocabulary: "Vocabulary",
    history: "History",
    profile: "Profile",
    settings: "Settings",
    logout: "Logout",
    language: "Language",
    nativeLanguage: "Interface Language",
    learningLanguage: "Learning Language",
    journalTitle: "Language Journal",
    newEntry: "New Entry",
    writePlaceholder: "Start writing...",
    review: "Review",
    save: "Save",
    analyzing: "Analyzing...",
    tutorName: "AI Tutor",
    askQuestion: "Ask a question...",
    loading: "Loading..."
  },
  es: {
    home: "Inicio",
    explore: "Explorar",
    stories: "Historias",
    journal: "Diario",
    vocabulary: "Vocabulario",
    history: "Historial",
    profile: "Perfil",
    settings: "Configuración",
    logout: "Cerrar Sesión",
    language: "Idioma",
    nativeLanguage: "Idioma de Interfaz",
    learningLanguage: "Idioma de Aprendizaje",
    journalTitle: "Diario de Idiomas",
    newEntry: "Nueva Entrada",
    writePlaceholder: "Empieza a escribir...",
    review: "Revisar",
    save: "Guardar",
    analyzing: "Analizando...",
    tutorName: "Tutor IA",
    askQuestion: "Haz una pregunta...",
    loading: "Cargando..."
  },
  fr: {
    home: "Accueil",
    explore: "Explorer",
    stories: "Histoires",
    journal: "Journal",
    vocabulary: "Vocabulaire",
    history: "Historique",
    profile: "Profil",
    settings: "Paramètres",
    logout: "Déconnexion",
    language: "Langue",
    nativeLanguage: "Langue d'interface",
    learningLanguage: "Langue d'apprentissage",
    journalTitle: "Journal Linguistique",
    newEntry: "Nouvelle Entrée",
    writePlaceholder: "Commencez à écrire...",
    review: "Réviser",
    save: "Enregistrer",
    analyzing: "Analyse en cours...",
    tutorName: "Tuteur IA",
    askQuestion: "Posez une question...",
    loading: "Chargement..."
  }
};

// Single source of truth for the whole app — includes es-419 (LatAm Spanish),
// pt-BR, and anything added to languagesConfig in future.
export const languageOptions = getLanguageOptions();

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const { user } = useAuth();

  // Target Language (What they are learning)
  const [selectedLanguage, setSelectedLanguage] = useState(localStorage.getItem('targetLanguage') || 'es');

  // Native Language (The UI Language)
  const [nativeLanguage, setNativeLanguage] = useState(localStorage.getItem('nativeLanguage') || 'en');

  const [userLanguageOptions, setUserLanguageOptions] = useState([]);

  // Initialize language from user preferences when user data is loaded
  useEffect(() => {
    if (user?.languages?.length > 0) {
      // If we haven't manually set a language yet, use the user's first learning language
      if (!localStorage.getItem('targetLanguage')) {
        setSelectedLanguage(user.languages[0].language_code);
      }

      // Filter options based on user's learning languages
      const userLearningCodes = user.languages.map(lang => lang.language_code);
      const filteredOptions = languageOptions.filter(lang =>
        userLearningCodes.includes(lang.code) || lang.code === 'en'
      );
      setUserLanguageOptions(filteredOptions);
    } else {
      setUserLanguageOptions(languageOptions);
    }
  }, [user]);

  useEffect(() => {
    if (selectedLanguage) {
      localStorage.setItem('targetLanguage', selectedLanguage);
    }
  }, [selectedLanguage]);

  useEffect(() => {
    if (nativeLanguage) {
      localStorage.setItem('nativeLanguage', nativeLanguage);
    }
  }, [nativeLanguage]);

  // Translation Helper Function (The missing piece causing the crash)
  const t = (key) => {
    const langDict = translations[nativeLanguage] || translations['en'];
    return langDict[key] || key;
  };

  const getLanguageDisplayName = (code) => {
    const language = languageOptions.find(lang => lang.code === code);
    return language ? language.name : code;
  };

  const getNativeLanguageName = () => {
    const language = languageOptions.find(lang => lang.code === nativeLanguage);
    return language ? language.name : 'English';
  };

  return (
    <LanguageContext.Provider value={{
      selectedLanguage,
      setSelectedLanguage,
      nativeLanguage,
      setNativeLanguage,
      languageOptions: userLanguageOptions.length > 0 ? userLanguageOptions : languageOptions,
      t, // Exporting t prevents the crash
      getLanguageDisplayName,
      getNativeLanguageName
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);